import { useParams, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  getGetHuntQueryOptions,
  getGetHuntLeaderboardQueryOptions,
} from '@cdf/api-client-react';
import { TrophyIcon, UsersIcon, ZapIcon, ActivityIcon, BoxesIcon, SmartphoneIcon, TargetIcon } from 'lucide-react';

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

const MEDALS = ['🥇', '🥈', '🥉'];

export function LeaderboardLivePage() {
  const { huntSlug } = useParams<{ huntSlug: string }>();

  const { data: hunt } = useQuery({
    ...getGetHuntQueryOptions(huntSlug ?? ''),
    enabled: !!huntSlug,
  });

  const { data: leaderboard } = useQuery({
    ...getGetHuntLeaderboardQueryOptions(huntSlug ?? ''),
    enabled: !!huntSlug,
    refetchInterval: 5000,
  });

  if (!hunt) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-slate-950 text-white">
        <p className="text-xl text-slate-400">Loading hunt…</p>
      </div>
    );
  }

  const isClosed = hunt.status === 'closed';
  const individuals = leaderboard?.individuals ?? [];
  const teams = leaderboard?.teams ?? [];
  const recent = leaderboard?.recentCaptures ?? [];
  const participantCount = leaderboard?.participantCount ?? 0;
  const totalCaptures = individuals.reduce((s, e) => s + e.captureCount, 0);
  const boothStations = hunt.stations.filter((s) => /Stand\s+\d+/i.test(s.name));
  const totalBooths = boothStations.length;

  return (
    <div className="min-h-dvh bg-slate-950 p-6 text-white md:p-10">
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .lb-row { animation: fadeInUp 0.4s ease-out; }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        .live-dot { animation: pulse-dot 1.5s ease-in-out infinite; }
      `}</style>

      {/* Header */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <TrophyIcon className="h-8 w-8 text-amber-400" />
            <h1 className="text-3xl font-extrabold tracking-tight md:text-5xl">
              {hunt.name}
            </h1>
          </div>
          {hunt.description && (
            <p className="mt-2 text-sm text-slate-400 md:text-base">{hunt.description}</p>
          )}
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="text-3xl font-bold tabular-nums md:text-4xl">{participantCount}</p>
            <p className="text-xs uppercase tracking-wider text-slate-500">Participants</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold tabular-nums md:text-4xl">{totalCaptures}</p>
            <p className="text-xs uppercase tracking-wider text-slate-500">Captures</p>
          </div>
          {!isClosed && (
            <div className="flex items-center gap-2 rounded-full bg-green-500/20 px-4 py-2">
              <span className="live-dot h-2.5 w-2.5 rounded-full bg-green-400" />
              <span className="text-sm font-medium text-green-300">Live</span>
            </div>
          )}
        </div>
      </div>

      {isClosed && (
        <div className="mb-8 rounded-xl border border-amber-400/40 bg-amber-500/10 px-6 py-4 text-center">
          <p className="text-lg font-bold text-amber-300 md:text-2xl">
            🏆 Final Results
          </p>
        </div>
      )}

      {/* Quick stats bar */}
      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-xl border border-slate-800 bg-slate-900/60 px-6 py-4">
        <div className="flex items-center gap-2">
          <TargetIcon className="h-5 w-5 text-sky-400" />
          <div>
            <p className="text-lg font-bold tabular-nums">{totalBooths}</p>
            <p className="text-xs uppercase tracking-wider text-slate-500">Exhibitor Booths</p>
          </div>
        </div>
        <div className="h-8 w-px bg-slate-800" />
        <div className="flex items-center gap-2">
          <BoxesIcon className="h-5 w-5 text-violet-400" />
          <div>
            <p className="text-lg font-bold tabular-nums">{hunt.stations.length}</p>
            <p className="text-xs uppercase tracking-wider text-slate-500">Total Stations</p>
          </div>
        </div>
        <div className="ml-auto flex gap-3">
          <Link to={`/events/${hunt.slug}/twin`} className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:bg-slate-800">
            <BoxesIcon className="h-4 w-4" />
            3D Twin
          </Link>
          <Link to="/ar" className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:bg-slate-800">
            <SmartphoneIcon className="h-4 w-4" />
            AR / CV Scanner
          </Link>
        </div>
      </div>

      {/* Leaderboard columns */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Individuals */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
          <div className="mb-4 flex items-center gap-2">
            <UsersIcon className="h-5 w-5 text-sky-400" />
            <h2 className="text-xl font-bold md:text-2xl">Top Individuals</h2>
          </div>
          <div className="space-y-2">
            {individuals.length === 0 ? (
              <p className="py-8 text-center text-slate-500">No participants yet</p>
            ) : (
              individuals.slice(0, 8).map((entry) => (
                <div
                  key={entry.participantId ?? entry.name}
                  className={`lb-row flex items-center justify-between rounded-lg px-4 py-3 ${
                    entry.rank === 1
                      ? 'bg-amber-500/15 border border-amber-400/30'
                      : 'bg-slate-800/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 text-center text-lg font-bold tabular-nums">
                      {entry.rank <= 3 ? MEDALS[entry.rank - 1] : entry.rank}
                    </span>
                    <div>
                      <p className="text-lg font-semibold md:text-xl">{entry.name}</p>
                      {entry.team && (
                        <p className="text-xs text-slate-400">{entry.team}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold tabular-nums md:text-2xl">
                      {entry.totalPoints}
                    </p>
                    <p className="text-xs text-slate-500">
                      {entry.captureCount} {entry.captureCount === 1 ? 'capture' : 'captures'}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Teams */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
          <div className="mb-4 flex items-center gap-2">
            <ZapIcon className="h-5 w-5 text-violet-400" />
            <h2 className="text-xl font-bold md:text-2xl">Top Teams</h2>
          </div>
          <div className="space-y-2">
            {teams.length === 0 ? (
              <p className="py-8 text-center text-slate-500">No teams yet</p>
            ) : (
              teams.slice(0, 8).map((entry) => (
                <div
                  key={entry.name}
                  className={`lb-row flex items-center justify-between rounded-lg px-4 py-3 ${
                    entry.rank === 1
                      ? 'bg-amber-500/15 border border-amber-400/30'
                      : 'bg-slate-800/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 text-center text-lg font-bold tabular-nums">
                      {entry.rank <= 3 ? MEDALS[entry.rank - 1] : entry.rank}
                    </span>
                    <div>
                      <p className="text-lg font-semibold md:text-xl">{entry.name}</p>
                      <p className="text-xs text-slate-400">
                        {entry.captureCount} {entry.captureCount === 1 ? 'capture' : 'captures'}
                      </p>
                    </div>
                  </div>
                  <p className="text-xl font-bold tabular-nums md:text-2xl">
                    {entry.totalPoints}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Recent captures ticker */}
      <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <div className="mb-4 flex items-center gap-2">
          <ActivityIcon className="h-5 w-5 text-emerald-400" />
          <h2 className="text-xl font-bold md:text-2xl">Recent Captures</h2>
        </div>
        {recent.length === 0 ? (
          <p className="py-4 text-center text-slate-500">No captures yet — be the first!</p>
        ) : (
          <div className="space-y-1.5">
            {recent.slice(0, 10).map((cap, i) => (
              <div
                key={`${cap.nickname}-${cap.capturedAt}-${i}`}
                className="lb-row flex items-center justify-between rounded-lg bg-slate-800/40 px-4 py-2.5"
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{cap.nickname}</span>
                  {cap.team && (
                    <span className="rounded bg-slate-700 px-2 py-0.5 text-xs text-slate-300">
                      {cap.team}
                    </span>
                  )}
                  <span className="text-slate-400">captured</span>
                  <span className="font-medium text-sky-300">{cap.stationName}</span>
                </div>
                <span className="text-xs text-slate-500">{relativeTime(cap.capturedAt)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {!isClosed && (
        <p className="mt-6 text-center text-xs text-slate-600">
          Auto-refreshing every 5 seconds
        </p>
      )}
    </div>
  );
}
